<script setup lang="ts">
import { ref, shallowRef, computed, watch, watchEffect } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import type { ReportDto } from '@/types'
import type { StepperItem } from '@nuxt/ui'
import { getLocalTimeZone, now, type ZonedDateTime } from '@internationalized/date'

import ReportContactFields from '@/components/report/ReportContactFields.vue'
import ReportAttachmentsField from '@/components/report/ReportAttachmentsField.vue'
import ReportDateTimeField from '@/components/report/ReportDateTimeField.vue'
import ReportStepperForm from '@/components/report/ReportStepperForm.vue'
import ReportDraftBanner from '@/components/report/ReportDraftBanner.vue'

import {
  bugReportSchema,
  featureReportSchema,
  bugSteps,
  featureSteps
} from '@/components/report/reportSchemas'
import { useReportSubmit } from '@/composables/useReportSubmit'
import { useReportDraft } from '@/composables/useReportDraft'

const authStore = useAuthStore()
const router = useRouter()

const feedbackCardUi = {
  root: 'h-full flex flex-col',
  body: 'flex flex-1 flex-col',
  footer: 'mt-auto'
}

const step = ref(0) // 0 = Hub, 1 = Bug Form, 2 = Feature Form
const selectedFiles = ref<File[]>([])
const dateValue = shallowRef<ZonedDateTime>(now(getLocalTimeZone()))

const reportStepperItems: StepperItem[] = [
  { slot: 'contact', title: 'Kontakt', icon: 'i-lucide-user' },
  { slot: 'details', title: 'Details', icon: 'i-lucide-file-text' },
  { slot: 'attachments', title: 'Anhänge & Absenden', icon: 'i-lucide-paperclip' }
]

function createInitialForm(type: 'bug' | 'feature'): ReportDto {
  return {
    type,
    contactPerson: authStore.userInfo?.user.fullName || '',
    phoneNumber: '',
    email: authStore.userInfo?.user.email || '',
    service: '',
    priority: undefined,
    description: '',
    observedBehavior: '',
    expectedBehavior: '',
    stepsToReproduce: '',
    businessContext: '',
    expectedBenefit: '',
    proposedSolution: '',
    additionalContext: '',
    businessImpact: '',
    startTime: dateValue.value ? dateValue.value.toDate().toLocaleString('de-DE') : new Date().toISOString(),
    dataLoss: false,
    securityImpact: false,
    productionOutage: false,
    venaboTicket: '',
    files: []
  }
}

const form = ref<ReportDto>(createInitialForm('bug'))

watch(
  dateValue,
  (val) => {
    if (val) {
      form.value.startTime = val.toDate().toLocaleString('de-DE')
    }
  },
  { immediate: true }
)

watchEffect(() => {
  if (authStore.userInfo?.user.fullName) {
    form.value.contactPerson = authStore.userInfo.user.fullName
  }
  if (authStore.userInfo?.user.email) {
    form.value.email = authStore.userInfo.user.email
  }
})

const bugDraft = useReportDraft('bug', form)
const featureDraft = useReportDraft('feature', form)
const { submitting, success, submit } = useReportSubmit()

function selectBug() {
  form.value = createInitialForm('bug')
  selectedFiles.value = []
  bugDraft.checkDraft()
  step.value = 1
}

function selectFeature() {
  form.value = createInitialForm('feature')
  selectedFiles.value = []
  featureDraft.checkDraft()
  step.value = 2
}

function selectEmergency() {
  router.push('/emergency')
}

function resetAndGoToHub() {
  success.value = false
  step.value = 0
}

async function handleReportSubmit() {
  try {
    const ok = await submit(form.value, selectedFiles.value, dateValue.value)
    if (ok) {
      if (form.value.type === 'bug') {
        bugDraft.clear()
      } else if (form.value.type === 'feature') {
        featureDraft.clear()
      }
    }
  } catch {
    // Error toast handled by useReportSubmit
  }
}

const headerIcon = computed(() => {
  if (step.value === 0) return 'i-lucide-help-circle'
  if (form.value.type === 'bug') return 'i-lucide-bug'
  return 'i-lucide-lightbulb'
})

const headerTitle = computed(() => {
  if (step.value === 0) return 'ConnectCloud Support & Feedback Hub'
  if (form.value.type === 'bug') return 'ConnectCloud Fehler melden'
  return 'ConnectCloud Funktion vorschlagen'
})
</script>

<template>
  <UPage>
    <UPageHeader
      :title="headerTitle"
      :icon="headerIcon"
      description="Wir schätzen Ihr Feedback! Wählen Sie eine Kategorie aus, um uns zu unterstützen."
      class="sticky top-0 z-20 border-b border-default bg-default/95 backdrop-blur"
    >
      <template #links>
        <UButton
          v-if="step === 0"
          to="/feedback/tickets"
          color="primary"
          icon="i-lucide-list"
          label="Ticketstatus ansehen"
          variant="subtle"
          class="mr-2"
        />
        <UButton
          v-if="step === 0"
          to="/"
          color="neutral"
          icon="i-lucide-arrow-left"
          label="Zurück zum Dashboard"
          variant="ghost"
        />
        <UButton
          v-else
          color="neutral"
          icon="i-lucide-arrow-left"
          label="Zurück zum Hub"
          variant="ghost"
          @click="resetAndGoToHub"
        />
      </template>
    </UPageHeader>

    <UPageBody>
      <div class="space-y-8">
        <div v-if="success" class="space-y-6">
          <div v-if="form.type === 'bug'">
            <UAlert
              color="success"
              variant="subtle"
              title="Fehlermeldung erhalten"
              icon="i-lucide-check-circle"
            >
              <template #description>
                Vielen Dank für Ihre Fehlermeldung! Wir haben Ihren Bericht erhalten und ein GitLab-Ticket erstellt. Unser Support-Team wird sich so schnell wie möglich darum kümmern.
              </template>
            </UAlert>
          </div>
          <div v-else>
            <UAlert
              color="success"
              variant="subtle"
              title="Vorschlag erhalten"
              icon="i-lucide-check-circle"
            >
              <template #description>
                Vielen Dank für Ihren Verbesserungsvorschlag! Ihre Idee wurde in unserem GitLab-System erfasst. Wir schätzen Ihr Feedback sehr.
              </template>
            </UAlert>
          </div>
          <div class="flex justify-end gap-3">
            <UButton to="/feedback/tickets" color="primary" variant="subtle" icon="i-lucide-list">Ticketstatus ansehen</UButton>
            <UButton color="neutral" variant="subtle" icon="i-lucide-help-circle" @click="resetAndGoToHub">Neues Anliegen / Hub</UButton>
            <UButton to="/" color="neutral" variant="ghost">Zurück zum Dashboard</UButton>
          </div>
        </div>

        <div v-else>
          <!-- Step 0: Selection Hub -->
          <div v-if="step === 0" class="space-y-8">
            <div class="rounded-2xl border border-neutral-200 bg-neutral-50/50 p-6 sm:p-8 dark:border-neutral-800 dark:bg-neutral-900/30">
              <div class="space-y-3">
                <UBadge color="primary" variant="subtle">Schnelle Orientierung</UBadge>
                <h2 class="text-2xl font-bold tracking-tight text-gray-900 dark:text-white">Wie können wir Ihnen helfen?</h2>
                <p class="text-gray-500 dark:text-gray-400">Wählen Sie die passende Kategorie aus. Kritische Vorfälle führen Sie direkt in den Notfallprozess, alle anderen Anliegen landen beim Support-Team.</p>
              </div>
            </div>

            <div class="grid items-stretch gap-6 md:grid-cols-3">
              <!-- Card 1: Emergency Report (Links to /emergency) -->
              <UCard 
                :ui="feedbackCardUi"
                class="h-full cursor-pointer border border-error-200 bg-error-50/30 transition-all hover:-translate-y-1 hover:shadow-md dark:border-error-900/50 dark:bg-error-950/10 flex flex-col justify-between"
                @click="selectEmergency"
              >
                <div class="p-2 flex flex-1 flex-col gap-4">
                  <div class="flex items-center gap-4">
                    <div class="inline-flex p-3 bg-error-100 dark:bg-error-900/30 text-error-600 rounded-2xl">
                      <UIcon name="i-lucide-life-buoy" class="w-7 h-7" />
                    </div>
                    <h3 class="text-lg font-bold text-error-700 dark:text-error-400">Notfall melden</h3>
                  </div>
                  <p class="flex-1 text-sm text-gray-600 dark:text-gray-300">
                    Kritische Störungen, Produktionsausfall, Sicherheitsvorfälle oder drohender Datenverlust.
                  </p>
                </div>
                <template #footer>
                  <UButton color="error" variant="soft" class="w-full justify-center" to="/emergency">Notfall melden</UButton>
                </template>
              </UCard>

              <!-- Card 2: Bug Report -->
              <UCard 
                :ui="feedbackCardUi"
                class="h-full cursor-pointer border border-warning-200 bg-warning-50/30 transition-all hover:-translate-y-1 hover:shadow-md dark:border-warning-900/50 dark:bg-warning-950/10 flex flex-col justify-between"
                @click="selectBug"
              >
                <div class="p-2 flex flex-1 flex-col gap-4">
                  <div class="flex items-center gap-4">
                    <div class="inline-flex p-3 bg-warning-100 dark:bg-warning-900/30 text-warning-600 rounded-2xl">
                      <UIcon name="i-lucide-bug" class="w-7 h-7" />
                    </div>
                    <h3 class="text-lg font-bold text-warning-700 dark:text-warning-400">Fehler melden</h3>
                  </div>
                  <p class="flex-1 text-sm text-gray-600 dark:text-gray-300">
                    Ein unerwartetes Verhalten oder ein Problem in einer Funktion von ConnectCloud.
                  </p>
                </div>
                <template #footer>
                  <UButton color="warning" variant="soft" class="w-full justify-center" @click.stop="selectBug">Fehler melden</UButton>
                </template>
              </UCard>

              <!-- Card 3: Feature Request -->
              <UCard 
                :ui="feedbackCardUi"
                class="h-full cursor-pointer border border-success-200 bg-success-50/30 transition-all hover:-translate-y-1 hover:shadow-md dark:border-success-900/50 dark:bg-success-950/10 flex flex-col justify-between"
                @click="selectFeature"
              >
                <div class="p-2 flex flex-1 flex-col gap-4">
                  <div class="flex items-center gap-4">
                    <div class="inline-flex p-3 bg-success-100 dark:bg-success-900/30 text-success-600 rounded-2xl">
                      <UIcon name="i-lucide-lightbulb" class="w-7 h-7" />
                    </div>
                    <h3 class="text-lg font-bold text-success-700 dark:text-success-400">Funktion vorschlagen</h3>
                  </div>
                  <p class="flex-1 text-sm text-gray-600 dark:text-gray-300">
                    Teilen Sie Ihre Ideen und Vorschläge zur Verbesserung von ConnectCloud mit uns.
                  </p>
                </div>
                <template #footer>
                  <UButton color="success" variant="soft" class="w-full justify-center" @click.stop="selectFeature">Idee einreichen</UButton>
                </template>
              </UCard>
            </div>
          </div>

          <!-- Step 1: Bug Form -->
          <div v-else-if="step === 1" class="space-y-6">
            <ReportDraftBanner
              v-if="bugDraft.hasDraft.value"
              @restore="bugDraft.restore"
              @discard="bugDraft.discard"
            />

            <ReportStepperForm
              :items="reportStepperItems"
              :schema="bugReportSchema"
              :state="form"
              :step-fields="(bugSteps as unknown as string[][])"
              :submitting="submitting"
              :show-visibility-hint="true"
              submit-button-text="Fehler melden"
              submit-button-color="primary"
              submit-button-icon="i-lucide-send"
              @submit="handleReportSubmit"
              @back="resetAndGoToHub"
            >
              <template #default="{ step: wizardStep }">
                <!-- Step 0: Contact -->
                <div v-if="wizardStep === 0">
                  <ReportContactFields
                    v-model="form"
                    service-label="Betroffener Service / Bereich"
                    service-placeholder="Service auswählen"
                  />
                </div>

                <!-- Step 1: Details -->
                <div v-else-if="wizardStep === 1" class="space-y-6">
                  <ReportDateTimeField
                    v-model="dateValue"
                    label="Zeitpunkt des Fehlers"
                    name="startTime"
                    :required="true"
                  />

                  <UFormField label="Beobachtetes Verhalten" name="observedBehavior" required class="w-full">
                    <UTextarea
                      v-model="form.observedBehavior"
                      autoresize
                      placeholder="Was ist passiert? Welches fehlerhafte Verhalten haben Sie beobachtet?"
                      class="w-full"
                      :rows="4"
                    />
                  </UFormField>

                  <UFormField label="Erwartetes Verhalten" name="expectedBehavior" class="w-full">
                    <UTextarea
                      v-model="form.expectedBehavior"
                      autoresize
                      placeholder="Was hätten Sie stattdessen erwartet?"
                      class="w-full"
                      :rows="3"
                    />
                  </UFormField>

                  <UFormField label="Schritte zur Reproduktion" name="stepsToReproduce" required class="w-full">
                    <UTextarea
                      v-model="form.stepsToReproduce"
                      autoresize
                      placeholder="1. Seite öffnen&#10;2. Klick auf Export&#10;3. Fehlermeldung '...' erscheint"
                      class="w-full"
                      :rows="4"
                    />
                  </UFormField>

                  <UFormField label="Weitere Details (optional)" name="additionalContext" class="w-full">
                    <UTextarea
                      v-model="form.additionalContext"
                      autoresize
                      placeholder="Weitere Informationen, z. B. Browser, Systemumgebung oder Fehlermeldungen..."
                      class="w-full"
                      :rows="3"
                    />
                  </UFormField>
                </div>

                <!-- Step 2: Attachments & Submit -->
                <div v-else-if="wizardStep === 2" class="space-y-6">
                  <p class="text-sm text-gray-600 dark:text-gray-300">
                    Laden Sie optional Screenshots, Logfiles oder relevante Dokumente hoch, die uns bei der Analyse helfen.
                  </p>
                  <ReportAttachmentsField v-model="selectedFiles" />
                </div>
              </template>
            </ReportStepperForm>
          </div>

          <!-- Step 2: Feature Form -->
          <div v-else-if="step === 2" class="space-y-6">
            <ReportDraftBanner
              v-if="featureDraft.hasDraft.value"
              @restore="featureDraft.restore"
              @discard="featureDraft.discard"
            />

            <ReportStepperForm
              :items="reportStepperItems"
              :schema="featureReportSchema"
              :state="form"
              :step-fields="(featureSteps as unknown as string[][])"
              :submitting="submitting"
              :show-visibility-hint="true"
              submit-button-text="Vorschlag einreichen"
              submit-button-color="primary"
              submit-button-icon="i-lucide-send"
              @submit="handleReportSubmit"
              @back="resetAndGoToHub"
            >
              <template #default="{ step: wizardStep }">
                <!-- Step 0: Contact -->
                <div v-if="wizardStep === 0">
                  <ReportContactFields
                    v-model="form"
                    service-label="Betreffendes Modul / Vorgeschlagener Bereich"
                    service-placeholder="Bereich auswählen"
                  />
                </div>

                <!-- Step 1: Details -->
                <div v-else-if="wizardStep === 1" class="space-y-6">
                  <UFormField label="Fachlicher Hintergrund" name="businessContext" required class="w-full">
                    <UTextarea
                      v-model="form.businessContext"
                      autoresize
                      placeholder="Beschreiben Sie den fachlichen Hintergrund und das Problem, das gelöst werden soll."
                      class="w-full"
                      :rows="4"
                    />
                  </UFormField>

                  <UFormField label="Erwarteter Nutzen" name="expectedBenefit" required class="w-full">
                    <UTextarea
                      v-model="form.expectedBenefit"
                      autoresize
                      placeholder="Wer profitiert davon und was ist der erwartete Mehrwert?"
                      class="w-full"
                      :rows="3"
                    />
                  </UFormField>

                  <UFormField label="Vorgeschlagene Lösung" name="proposedSolution" class="w-full">
                    <UTextarea
                      v-model="form.proposedSolution"
                      autoresize
                      placeholder="Wie sollte das Feature funktionieren? Gerne auch UI/UX Ideen oder Mockups."
                      class="w-full"
                      :rows="3"
                    />
                  </UFormField>

                  <UFormField label="Weitere Details" name="additionalContext" class="w-full">
                    <UTextarea
                      v-model="form.additionalContext"
                      autoresize
                      placeholder="Links, Referenzen oder zusätzliche Notizen"
                      class="w-full"
                      :rows="3"
                    />
                  </UFormField>
                </div>

                <!-- Step 2: Attachments & Submit -->
                <div v-else-if="wizardStep === 2" class="space-y-6">
                  <p class="text-sm text-gray-600 dark:text-gray-300">
                    Laden Sie optional Skizzen, Beispieldokumente oder Mockups hoch, um Ihren Vorschlag zu veranschaulichen.
                  </p>
                  <ReportAttachmentsField v-model="selectedFiles" />
                </div>
              </template>
            </ReportStepperForm>
          </div>
        </div>
      </div>
    </UPageBody>
  </UPage>
</template>
